// FILE: src/services/dashboardService.js
//
// Aggregation service for the Admin Dashboard.
//
// Uses the same loading strategy as the QuestionEditor:
//   1. Try loadQuestionSummariesFromSupabase() for each exam (migration 099
//      projection — ids/labels/preview/has_explanation, never stems or choices).
//   2. Fall back to loadBundledCertBank() (bundled JSON via dynamic import) if Supabase returns nothing.
//
// This guarantees that the dashboard totals always match what the Question
// Bank shows — whether the full question set lives in Supabase or in the
// bundled JSON fallback.
//
// A separate Supabase query fetches the last 50 question_history rows for the
// activity feed.  That feed is always Supabase-only (bundled JSON has no history).
//
// A further Supabase query on exam_sessions powers the session-metrics panel.
//
// ── Return shape ───────────────────────────────────────────────────────────────
//
//  {
//    questions:      { pmp: NormalizedQuestion[], cpmai: NormalizedQuestion[] },
//    metrics:        { pmp: ExamMetrics, cpmai: ExamMetrics },
//    overall:        ExamMetrics,
//    recentActivity: ActivityEntry[],
//    sessionMetrics: { pmp: SessionMetrics, cpmai: SessionMetrics },
//  }
//
// ── ExamMetrics shape ──────────────────────────────────────────────────────────
//
//  {
//    examId:     string,
//    total:      number,
//    byStatus:   { new, active, retired },
//    byReview:   { unreviewed, reported, in_review, reviewed },
//    byType:     { multiple_choice, multiple_select, visual_interpretation, matching, hotspot, fill_in_blank, unknown },
//    byCategory: { [label]: number, '(uncategorized)': number, '(invalid)': number },
//    byPerformanceDomain: { [label]: number, '(unassigned)': number }, // PMP only — {} for others
//    byDeliveryContext:   { [label]: number, '(unassigned)': number }, // PMP only — {} for others
//    quality:    { missingExplanation, noCategory, invalidCategory, awaitingApproval },
//  }
//
// performanceDomain and deliveryContext are PMP-only in this increment.
// For non-PMP exams the two aggregates are returned as empty objects {}.
//
// ── SessionMetrics shape ───────────────────────────────────────────────────────
//
//  Per-exam metrics are split cleanly between full-exam and practice sessions
//  so the dashboard can present "exam readiness" and "practice / learning
//  activity" as two independent pictures with no mixed averages.
//
//  {
//    fullCount:              number,  // total full-exam sessions  (= full.count)
//    practiceCount:          number,  // total practice sessions   (= practice.count)
//    totalQuestionsAnswered: number,  // answered_count summed across all modes
//    totalDurationSeconds:   number,  // ended_at - started_at, summed across all modes
//
//    full: {
//      count:              number,
//      passCount:          number,      // completed full sessions with score >= PASS_THRESHOLD
//      failCount:          number,      // completed full sessions with score <  PASS_THRESHOLD
//      earlyExitCount:     number,      // full sessions with completion_reason === 'early_exit'
//      avgScorePct:        number|null, // average score % across full sessions (filtered to ≥ MIN_COMPLETION_PCT)
//      avgCompletionPct:   number|null, // average completion % across full sessions (filtered)
//      bestScorePct:       number|null, // best score % across full sessions
//      mostRecentScorePct: number|null, // score % of the most recent full session (by started_at)
//    },
//
//    practice: {
//      count:              number,
//      earlyExitCount:     number,      // practice sessions with completion_reason === 'early_exit'
//      avgScorePct:        number|null, // average score % across practice sessions (filtered)
//      avgCompletionPct:   number|null, // average completion % across practice sessions (filtered)
//      questionsAnswered:  number,      // answered_count summed across practice sessions
//    },
//  }
//
//  No top-level mixed averages are exposed — pass/fail and score averages live
//  inside `full` or `practice` only, so the UI cannot accidentally combine
//  exam-readiness data with practice-activity data.
//
// All NormalizedQuestion objects use camelCase (same shape as normalizeSupabaseQuestion).

import { supabase } from './supabaseClient';
import { getAllExams } from '../config/exams';
import { loadQuestionSummariesFromSupabase, fetchAllPaginated } from './questionService';
import { loadBundledCertBank } from './certBankLoader';
import {
  EXAM_CATEGORIES,
  PMP_PERFORMANCE_DOMAINS,
  PMP_DELIVERY_CONTEXTS,
} from '../config/examCategories';
import { QUESTION_STATUSES, REVIEW_STATUSES } from '../config/lifecycleConstants';
import { buildSecondaryDistribution } from './secondaryDimensionService';

// ── Pass threshold ────────────────────────────────────────────────────────────

/** Minimum score % (inclusive) that counts as a pass. */
export const PASS_THRESHOLD = 80;

/**
 * Sessions with fewer than this many percent of questions answered are excluded
 * from completion and score averages to avoid skewing results with abandoned
 * sessions.
 */
const MIN_COMPLETION_PCT = 20;

// ── Question type catalogue ────────────────────────────────────────────────────

export const QUESTION_TYPES = [
  { key: 'multiple_choice',       label: 'Multiple Choice' },
  { key: 'multiple_select',       label: 'Multiple Select' },
  { key: 'visual_interpretation', label: 'Visual Interpretation' },
  { key: 'matching',              label: 'Matching' },
  { key: 'hotspot',               label: 'Hotspot' },
  { key: 'fill_in_blank',         label: 'Fill in Blank' },
];

// ── Change type metadata ──────────────────────────────────────────────────────

export const CHANGE_TYPE_META = {
  create:  { label: 'Imported',  icon: '📥', color: '#1565c0' },
  update:  { label: 'Edited',    icon: '✏️',  color: '#555' },
  review:  { label: 'Reviewed',  icon: '✅',  color: '#2e7d32' },
  retire:  { label: 'Retired',   icon: '🗃️',  color: '#777' },
  restore: { label: 'Restored',  icon: '♻️',  color: '#6a1b9a' },
};

// ── Internal helpers ──────────────────────────────────────────────────────────

function zeroCounts(keys) {
  return Object.fromEntries(keys.map((k) => [k, 0]));
}

/**
 * Computes all metrics for a single exam from normalized question objects.
 * Fields are camelCase (same shape returned by normalizeSupabaseQuestion /
 * the bundled-JSON normalizeQuestion).
 *
 * @param {object[]} questions  - normalized question objects
 * @param {string}   examId     - 'pmp' | 'cpmai'
 * @returns {object} ExamMetrics
 */
export function buildExamMetrics(questions, examId) {
  const knownCats = EXAM_CATEGORIES[examId] || [];
  const typeKeys  = QUESTION_TYPES.map((t) => t.key);

  const byStatus   = zeroCounts(QUESTION_STATUSES);
  const byReview   = zeroCounts(REVIEW_STATUSES);
  const byType     = { ...zeroCounts(typeKeys), unknown: 0 };
  const byCategory = { ...zeroCounts(knownCats), '(uncategorized)': 0, '(invalid)': 0 };

  // ── PMP-only taxonomy buckets ─────────────────────────────────────────────
  // For non-PMP exams these aggregates are intentionally empty — the editor
  // and dashboard both hide these dimensions outside of PMP in this increment.
  const isPmp = examId === 'pmp';
  const byPerformanceDomain = isPmp
    ? { ...zeroCounts(PMP_PERFORMANCE_DOMAINS), '(unassigned)': 0 }
    : {};
  const byDeliveryContext = isPmp
    ? { ...zeroCounts(PMP_DELIVERY_CONTEXTS), '(unassigned)': 0 }
    : {};

  let missingExplanation = 0;
  let noCategory         = 0;
  let invalidCategory    = 0;
  let awaitingApproval   = 0;

  for (const q of questions) {
    // ── Status (camelCase: q.status) ──────────────────────────────────────────
    const s = q.status || 'active';
    byStatus[s] = (byStatus[s] ?? 0) + 1;

    // ── Review status (camelCase: q.reviewStatus) ─────────────────────────────
    const rs = q.reviewStatus || 'unreviewed';
    byReview[rs] = (byReview[rs] ?? 0) + 1;

    // ── Question type ─────────────────────────────────────────────────────────
    const t = q.type || 'multiple_choice';
    if (t in byType) {
      byType[t]++;
    } else {
      byType.unknown++;
    }

    // ── Category ──────────────────────────────────────────────────────────────
    const cat = (q.category || '').trim();
    if (!cat) {
      byCategory['(uncategorized)']++;
      noCategory++;
    } else if (knownCats.length > 0 && !knownCats.includes(cat)) {
      byCategory['(invalid)']++;
      invalidCategory++;
    } else {
      byCategory[cat] = (byCategory[cat] ?? 0) + 1;
    }

    // ── PMP-only taxonomy buckets ────────────────────────────────────────────
    if (isPmp) {
      const pd = (q.performanceDomain || '').trim();
      if (!pd) byPerformanceDomain['(unassigned)']++;
      else if (PMP_PERFORMANCE_DOMAINS.includes(pd)) byPerformanceDomain[pd]++;
      else byPerformanceDomain['(unassigned)']++;

      const dc = (q.deliveryContext || '').trim();
      if (!dc) byDeliveryContext['(unassigned)']++;
      else if (PMP_DELIVERY_CONTEXTS.includes(dc)) byDeliveryContext[dc]++;
      else byDeliveryContext['(unassigned)']++;
    }

    // ── Data quality ──────────────────────────────────────────────────────────
    if (isMissingExplanation(q)) missingExplanation++;
    if (q.status === 'new') awaitingApproval++;
  }

  // ── Secondary dimension distribution (config-driven) ──────────────────────
  // Gated by getSecondaryDimension(examId): null for certs without one, so the
  // admin surface stays empty for pmp / cpmai / pmi-acp. No per-cert branching.
  const byFocusArea = buildSecondaryDistribution(questions, examId);

  return {
    examId,
    total: questions.length,
    byStatus,
    byReview,
    byType,
    byCategory,
    byPerformanceDomain,
    byDeliveryContext,
    // Generic secondary-dimension distribution (e.g. PMOCP focus_area) or null.
    byFocusArea,
    quality: { missingExplanation, noCategory, invalidCategory, awaitingApproval },
  };
}

/**
 * Cross-exam aggregate for the "All Exams" tab.
 * byCategory is intentionally empty (categories differ per exam).
 */
function buildOverallMetrics(metrics, examIds, totalCount) {
  const typeKeys = QUESTION_TYPES.map((t) => t.key);
  const byStatus = zeroCounts(QUESTION_STATUSES);
  const byReview = zeroCounts(REVIEW_STATUSES);
  const byType   = { ...zeroCounts(typeKeys), unknown: 0 };
  let missingExplanation = 0;
  let noCategory         = 0;
  let invalidCategory    = 0;
  let awaitingApproval   = 0;

  for (const id of examIds) {
    const m = metrics[id];
    for (const k of QUESTION_STATUSES) byStatus[k] += m.byStatus[k] || 0;
    for (const k of REVIEW_STATUSES)   byReview[k] += m.byReview[k] || 0;
    for (const k of typeKeys)          byType[k]   += m.byType[k]   || 0;
    byType.unknown     += m.byType.unknown || 0;
    missingExplanation += m.quality.missingExplanation;
    noCategory         += m.quality.noCategory;
    invalidCategory    += m.quality.invalidCategory;
    awaitingApproval   += m.quality.awaitingApproval;
  }

  return {
    examId: 'all',
    total: totalCount,
    byStatus,
    byReview,
    byType,
    byCategory: {},
    // PMP-only dimensions are not aggregated cross-exam; see per-exam metrics.
    byPerformanceDomain: {},
    byDeliveryContext:   {},
    // Secondary dimension is per-cert; not aggregated cross-exam.
    byFocusArea: null,
    quality: { missingExplanation, noCategory, invalidCategory, awaitingApproval },
  };
}

/**
 * Queries exam_sessions from Supabase and computes per-exam usage, outcome,
 * and performance aggregates.
 *
 * Sessions where (answered_count / total_questions) < MIN_COMPLETION_PCT are
 * excluded from the completion and score averages but are still counted in the
 * usage/outcome totals.
 *
 * @param {string[]} examIds
 * @returns {Promise<{ [examId: string]: SessionMetrics }>}
 */
async function loadSessionMetrics(examIds) {
  // Build a zero-value result for every exam so callers always get a full object.
  const zeroFullBlock = () => ({
    count:              0,
    passCount:          0,
    failCount:          0,
    earlyExitCount:     0,
    avgScorePct:        null,
    avgCompletionPct:   null,
    bestScorePct:       null,
    mostRecentScorePct: null,
  });

  const zeroPracticeBlock = () => ({
    count:             0,
    earlyExitCount:    0,
    avgScorePct:       null,
    avgCompletionPct:  null,
    questionsAnswered: 0,
  });

  const zeroEntry = () => ({
    fullCount:              0,
    practiceCount:          0,
    totalQuestionsAnswered: 0,
    totalDurationSeconds:   0,
    full:                   zeroFullBlock(),
    practice:               zeroPracticeBlock(),
  });

  try {
    // Paginate: exam_sessions grows over time and will eventually exceed
    // the 1000-row PostgREST default cap.  Order by `id` (uuid PK, NOT
    // NULL, unique) so page boundaries remain stable between successive
    // range calls regardless of timestamp collisions.
    //
    // NOTE: only select columns that exist on exam_sessions.  The schema
    // uses started_at / ended_at — there is no `created_at`.  Selecting
    // or ordering by a non-existent column makes PostgREST return an
    // error, the catch below swallows it into zero entries, and the
    // dashboard renders "No exam sessions recorded yet." See
    // dashboardService.test.js for the regression guard.
    const { data: rows, error } = await fetchAllPaginated(
      () =>
        supabase
          .from('exam_sessions')
          .select('exam_id, mode_name, total_questions, answered_count, score_percentage, completion_reason, started_at, ended_at')
          .in('exam_id', examIds)
          .gt('answered_count', 0)
          // Exclude automated Playwright / CI runs from dashboard metrics.
          // Sessions are tagged on insert via REACT_APP_SESSION_SOURCE; real
          // Netlify builds leave the flag unset and insert source='user'.
          .eq('source', 'user')
          .order('id', { ascending: true }),
      'loadSessionMetrics'
    );

    if (error) throw error;

    // Accumulator includes temporary arrays for averages and trackers for
    // best / most-recent scores per exam.
    const acc = Object.fromEntries(
      examIds.map((id) => [id, {
        ...zeroEntry(),
        _fullCompletionSamples:     [],
        _fullScoreSamples:          [],
        _practiceCompletionSamples: [],
        _practiceScoreSamples:      [],
        _mostRecentFullStartedAt:   null,
        _mostRecentFullScore:       null,
      }])
    );

    for (const row of (rows || [])) {
      const m = acc[row.exam_id];
      if (!m) continue; // unknown exam_id — skip

      const completionPct =
        row.total_questions > 0
          ? (row.answered_count / row.total_questions) * 100
          : 0;

      const hasScore =
        row.score_percentage !== null && row.score_percentage !== undefined;

      // ── Cross-mode activity totals ─────────────────────────────────────────
      m.totalQuestionsAnswered += row.answered_count || 0;

      if (row.started_at && row.ended_at) {
        const durSec = Math.max(
          0,
          Math.round(
            (new Date(row.ended_at).getTime() - new Date(row.started_at).getTime()) / 1000
          )
        );
        if (Number.isFinite(durSec)) m.totalDurationSeconds += durSec;
      }

      // ── Per-mode bucket ────────────────────────────────────────────────────
      if (row.mode_name === 'full') {
        m.fullCount++;
        m.full.count++;

        if (row.completion_reason === 'early_exit') {
          m.full.earlyExitCount++;
        } else if (hasScore) {
          if (row.score_percentage >= PASS_THRESHOLD) m.full.passCount++;
          else                                        m.full.failCount++;
        }

        if (completionPct >= MIN_COMPLETION_PCT) {
          m._fullCompletionSamples.push(completionPct);
          if (hasScore) m._fullScoreSamples.push(row.score_percentage);
        }

        if (hasScore) {
          if (m.full.bestScorePct === null || row.score_percentage > m.full.bestScorePct) {
            m.full.bestScorePct = row.score_percentage;
          }
          // Track most-recent full session by started_at; rows with no
          // started_at are skipped from "most recent" so older un-timestamped
          // rows can't masquerade as the latest.
          if (row.started_at) {
            if (
              m._mostRecentFullStartedAt === null ||
              new Date(row.started_at).getTime() > new Date(m._mostRecentFullStartedAt).getTime()
            ) {
              m._mostRecentFullStartedAt = row.started_at;
              m._mostRecentFullScore     = row.score_percentage;
            }
          }
        }
      } else if (row.mode_name === 'practice') {
        m.practiceCount++;
        m.practice.count++;
        m.practice.questionsAnswered += row.answered_count || 0;

        if (row.completion_reason === 'early_exit') {
          m.practice.earlyExitCount++;
        }

        if (completionPct >= MIN_COMPLETION_PCT) {
          m._practiceCompletionSamples.push(completionPct);
          if (hasScore) m._practiceScoreSamples.push(row.score_percentage);
        }
      }
    }

    // Collapse accumulator arrays into averages.
    const avg = (arr) =>
      arr.length > 0
        ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length)
        : null;

    const result = {};
    for (const [id, m] of Object.entries(acc)) {
      result[id] = {
        fullCount:              m.fullCount,
        practiceCount:          m.practiceCount,
        totalQuestionsAnswered: m.totalQuestionsAnswered,
        totalDurationSeconds:   m.totalDurationSeconds,
        full: {
          count:              m.full.count,
          passCount:          m.full.passCount,
          failCount:          m.full.failCount,
          earlyExitCount:     m.full.earlyExitCount,
          avgScorePct:        avg(m._fullScoreSamples),
          avgCompletionPct:   avg(m._fullCompletionSamples),
          bestScorePct:       m.full.bestScorePct,
          mostRecentScorePct: m._mostRecentFullScore,
        },
        practice: {
          count:             m.practice.count,
          earlyExitCount:    m.practice.earlyExitCount,
          avgScorePct:       avg(m._practiceScoreSamples),
          avgCompletionPct:  avg(m._practiceCompletionSamples),
          questionsAnswered: m.practice.questionsAnswered,
        },
      };
    }
    return result;
  } catch (err) {
    console.warn('[dashboardService] Session metrics fetch failed:', err);
    return Object.fromEntries(examIds.map((id) => [id, zeroEntry()]));
  }
}

/**
 * True when a question has no explanation text.
 *
 * Two row shapes reach the dashboard and they carry this differently:
 *   • questions_admin_summary rows (the normal path, migration 099) carry a
 *     `hasExplanation` boolean — the explanation text itself never crosses the
 *     wire, which is the whole point of the projection.
 *   • bundled JSON fallback banks carry the full `explanation` string.
 *
 * Checking `hasExplanation` first and only then falling back keeps the metric
 * identical for both, and keeps a fallback bank from reporting every question
 * as missing an explanation.
 *
 * @param {object} q
 * @returns {boolean}
 */
export function isMissingExplanation(q) {
  if (!q) return true;
  if (typeof q.hasExplanation === 'boolean') return !q.hasExplanation;
  return !q.explanation || !String(q.explanation).trim();
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Loads all dashboard data.
 *
 * For each exam:
 *   - Calls loadQuestionSummariesFromSupabase() (all statuses, normalized camelCase).
 *   - Falls back to loadBundledCertBank() (bundled JSON, dynamic import) if Supabase returns nothing.
 *
 * Totals will therefore always match the Question Bank regardless of how many
 * questions have been seeded into Supabase.
 *
 * @returns {Promise<{ questions, metrics, overall, recentActivity }>}
 */
export async function loadDashboardData() {
  const exams   = getAllExams();
  const examIds = exams.map((e) => e.examId);

  // ── Load questions (Supabase → JSON fallback) per exam ────────────────────
  const questionsByExam = {};
  await Promise.all(
    examIds.map(async (examId) => {
      // Summary projection, not the full bank — see migration 099. Falls back
      // to the bundled bank on null exactly as the full loader did.
      const supabaseRows = await loadQuestionSummariesFromSupabase(examId);
      questionsByExam[examId] =
        supabaseRows && supabaseRows.length > 0
          ? supabaseRows
          : await loadBundledCertBank(examId);
    })
  );

  // ── Fetch recent history and session metrics in parallel ─────────────────
  let recentActivity = [];
  let sessionMetrics = Object.fromEntries(examIds.map((id) => [id, {
    fullCount:              0,
    practiceCount:          0,
    totalQuestionsAnswered: 0,
    totalDurationSeconds:   0,
    full: {
      count: 0, passCount: 0, failCount: 0, earlyExitCount: 0,
      avgScorePct: null, avgCompletionPct: null,
      bestScorePct: null, mostRecentScorePct: null,
    },
    practice: {
      count: 0, earlyExitCount: 0,
      avgScorePct: null, avgCompletionPct: null,
      questionsAnswered: 0,
    },
  }]));

  await Promise.all([
    // Recent question history
    (async () => {
      try {
        const { data: historyRows } = await supabase
          .from('question_history')
          .select('id, question_id, change_type, changed_at, change_note, version_no')
          .order('changed_at', { ascending: false })
          .limit(50);

        // Build a map of questionId → examId for annotation
        const qExamMap = {};
        for (const [examId, qs] of Object.entries(questionsByExam)) {
          for (const q of qs) qExamMap[String(q.id)] = examId;
        }

        recentActivity = (historyRows || []).map((h) => ({
          id:         h.id,
          questionId: h.question_id,
          examId:     qExamMap[h.question_id] || null,
          changeType: h.change_type,
          changedAt:  h.changed_at,
          changeNote: h.change_note,
          versionNo:  h.version_no,
        }));
      } catch (err) {
        // History is best-effort — don't fail the whole dashboard if it errors.
        console.warn('[dashboardService] History fetch failed:', err);
      }
    })(),

    // Exam session metrics
    (async () => {
      sessionMetrics = await loadSessionMetrics(examIds);
    })(),
  ]);

  // ── Build metrics ─────────────────────────────────────────────────────────
  const metrics = Object.fromEntries(
    examIds.map((id) => [id, buildExamMetrics(questionsByExam[id], id)])
  );

  const totalCount = examIds.reduce((sum, id) => sum + questionsByExam[id].length, 0);
  const overall    = buildOverallMetrics(metrics, examIds, totalCount);

  return { questions: questionsByExam, metrics, overall, recentActivity, sessionMetrics };
}
